import { Separator } from "@/components/ui/separator";
import { Clock, Utensils } from "lucide-react";
import type { ReactNode } from "react";
import { IngredientHighlighter } from "@/lib/ingredient-highlighter";

export type RecipeMetaSidebarProps = Readonly<{
  prepTime?: string | null;
  cookTime?: string | null;
  servings?: string | null;
  ingredients: string[];
}>;

export const RecipeMetaSidebar = ({ prepTime, cookTime, servings, ingredients }: RecipeMetaSidebarProps) => {
  return (
    <aside className="md:col-span-2">
      <div className="sticky top-24 space-y-6 rounded-lg bg-background/60 p-5 shadow-sm">
        <div className="grid grid-cols-3 gap-4 text-center">
          {prepTime ? <TimeStat icon={<Clock className="mx-auto h-6 w-6 text-primary" />} label="Prep" value={prepTime} /> : null}
          {cookTime ? <TimeStat icon={<Clock className="mx-auto h-6 w-6 text-primary" />} label="Cook" value={cookTime} /> : null}
          {servings ? <TimeStat icon={<Utensils className="mx-auto h-6 w-6 text-primary" />} label="Serves" value={servings} /> : null}
        </div>

        <div>
          <h2 className="font-headline text-2xl font-semibold">Ingredients</h2>
          <Separator className="my-3" />
          <ul className="space-y-3 text-foreground">
            {ingredients.length > 0 ? (
              ingredients.map((item) => {
                const isHeader = item.trim().startsWith("##");
                const key = item.trim().toLowerCase() || `ingredient-${item.length}`;

                if (isHeader) {
                  return (
                    <li key={key} className="list-none pt-3 pb-1 font-semibold text-foreground">
                      {item.trim().replace(/^##\s*/, "")}
                    </li>
                  );
                }

                return (
                  <li key={key} className="flex items-start gap-2">
                    <span className="mt-2 block h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                    <span>{IngredientHighlighter.renderHighlightedIngredient(item)}</span>
                  </li>
                );
              })
            ) : (
              <li className="text-sm text-muted-foreground">No ingredients yet.</li>
            )}
          </ul>
        </div>
      </div>
    </aside>
  );
};

type TimeStatProps = {
  icon: ReactNode;
  label: string;
  value: string;
};

const TimeStat = ({ icon, label, value }: TimeStatProps) => {
  return (
    <div className="space-y-1">
      {icon}
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium text-foreground">{value}</p>
    </div>
  );
};

